import { I18n } from '@calendly/i18n'
import { textColorL3 } from '@calendly/ui/theme'
import { groupBy, isEmpty, camelCase, isString } from 'lodash-es'
import moment from 'moment-timezone'

import { convertHostIdsToMeetingRoles } from 'components/meetings'
import {
  ANALYTICS_MAP,
  allValue,
  allowedScopeTypes,
  nonHostValue,
  Periods,
  SchedulingMethods,
  Statuses
} from 'constants/meetings'
import { getVideoLocationKinds, getVideoLocationTypes } from 'features/meeting_intelligence'
import { camelize } from 'services/string_utils'
import { mapScopeTypeToBackendValue } from 'shared/api/meetings/utils'


// Re-export from canonical source (used by features/meetings/index.js)
export {
  isPendingEvent,
  transformLocationKind,
} from 'components/meetings'

export { mapScopeTypeToBackendValue }

export const buildSearchFilters = (scopeType, scopeId, filterParams) => {
  if (!filterParams) return null

  const validFilterProperties = new Set([
    'period',
    'past',
    'startDate',
    'endDate',
    'inviteeEmails',
    'eventTypeIds',
    'packageUuids',
    'schedulingMethods',
    'statusIds',
    'uuid',
    'userIds',
    'teamIds',
    'utmCampaign',
    'utmContent',
    'utmMedium',
    'utmSource',
    'utmTerm',
    'scopeType',
    'scopeId',
    'searchKeyword',
    'meetingIntelligenceStatus'
  ])

  const camelizedParams = camelize({
    ...filterParams,
    scopeType,
    scopeId,
  })

  const filters = {}
  const roles = convertHostIdsToMeetingRoles(filterParams.host_ids, scopeType, scopeId)
  if (roles) {
    filters.role = roles
  }

  if (scopeType === 'users' && !scopeId) {
    filters.ownerType = 'USER'
  } else if (scopeType === 'teams' && !scopeId) {
    filters.ownerType = 'TEAM'
  }

  if (filterParams.start_date && filterParams.end_date) {
    filters.startDate = filterParams.start_date
    filters.endDate = filterParams.end_date
  }

  for (const [key, value] of Object.entries(camelizedParams)) {
    if (validFilterProperties.has(key) && value != null && (!Array.isArray(value) || value.length > 0)) {
      filters[key] = value
    }
  }

  if (filterParams.search_keyword) {
    filters.searchKeyword = filterParams.search_keyword
  }

  if (filters?.scopeType) {
    filters.scopeType = mapScopeTypeToBackendValue(filters.scopeType, filters?.scopeId)

    if (!allowedScopeTypes.includes(filters.scopeType)) {
      filters.scopeType = ''
    }

    if (filters.scopeType === 'my_calendly') {
      filters.scopeId = ''
    }
  }

  if (!(scopeType === 'user' && scopeId === 'me')) {
    filters.schedulingMethods = [SchedulingMethods.CALENDLY]
  }

  return filters
}

export const isSupportedLocation = (event, hasMsTeamsFlag = false) => {
  const eligibleLocationKinds = getVideoLocationKinds(hasMsTeamsFlag)
  const eligibleLocationTypes = getVideoLocationTypes(hasMsTeamsFlag)
  return eligibleLocationKinds.includes(event?.locationKind)
    || eligibleLocationTypes.includes(event?.locationType)
    || eligibleLocationKinds.includes(event?.location_kind)
    || eligibleLocationTypes.includes(event?.location_type)
}

/** Solo vs group for list UI: trust API event_type.kind when group (group ET may have only one booking). */
const listKindFromEventTypeAndInvitees = (camelEventType, inviteeCount) => {
  const raw = camelEventType?.kind
  if (typeof raw === 'string' && raw.toLowerCase() === 'group') return 'group'
  return inviteeCount > 1 ? 'group' : 'solo'
}

export const mapNodeToMeeting = (node, publisherTZ, hasMsTeamsFlag = false, currentTime = moment()) => {
  let attendees = (node.attendees || []).map((attendee) => ({ ...attendee, policy: { noShow: false, update: false } }))
  // Supergraph (and some detail payloads) expose a singular `invitee` instead of `attendees`.
  if (attendees.length === 0 && node.invitee && typeof node.invitee === 'object') {
    const camelInvitee = camelize(node.invitee)
    attendees = [{
      ...camelInvitee,
      kind: camelInvitee.kind || 'INVITEE',
      policy: { noShow: false, update: false },
    }]
  }

  const groupedAttendees = groupBy(attendees, 'kind')
  const allInvitees = groupedAttendees.INVITEE || []
  const invitees = allInvitees.filter((inv) => !inv.cancelled)
  const hosts = groupedAttendees.HOST || []
  const cohosts = groupedAttendees.COHOST || []
  const externalAttendees = groupedAttendees.EXTERNAL || []
  const eventTypeBase = camelize(node.event_type || {})

  const serverPolicy = camelize(node.policy || {})
  const policy = {
    update: false,
    ...serverPolicy,
    addAiNotetaker: isSupportedLocation(node, hasMsTeamsFlag) && !node.cancelled,
  }

  const filledFromNode = node.filled_count ?? node.filledCount
  const filledCountFromApi = filledFromNode != null && filledFromNode !== ''
  const filledCount = filledCountFromApi
    ? filledFromNode
    : invitees.length

  const updated = {
    ...camelize(node),
    startTime: moment(node.start_time || node.startTime).tz(publisherTZ).format(),
    endTime: moment(node.end_time || node.endTime).tz(publisherTZ).format(),
    source: node.kind,
    eventType: {
      ...eventTypeBase,
      // Only treat as external (generic color) when kind is explicitly non-Calendly.
      // Missing `kind` (e.g. some detail queries) must not trigger this — `undefined !== 'CALENDLY'` is true.
      ...(node.kind != null && node.kind !== 'CALENDLY' ? { color: textColorL3 } : {}),
      kind: listKindFromEventTypeAndInvitees(eventTypeBase, allInvitees.length),
    },
    policy,
    invitee: invitees[0] || {},
    filledCount,
    // When the API omits filled_count, we derive from invitees; thin payloads can yield 0 incorrectly.
    // defensiveMergeMeeting uses this to avoid clobbering a known-good REST/cache count with that 0.
    ...(filledCountFromApi ? {} : { filledCountDerivedFromInvitees: true }),
    eventMemberships: hosts
      .concat(cohosts)
      .concat(externalAttendees)
      .filter(Boolean),
    invitees,
    attendees,
    host: hosts[0],
    past: moment(node.end_time || node.endTime).isBefore(currentTime),
  }
  return updated
}

export const mapChangedFiltersForAnalytics = (changedFilters) => {
  if (isEmpty(changedFilters)) return []
  if (!ANALYTICS_MAP) return []

  return changedFilters
    .map(camelCase)
    .filter((filter) => filter in ANALYTICS_MAP)
    .map((filter) => ANALYTICS_MAP[filter])
}

const UTM_PARAMS = ['utm_campaign', 'utm_content', 'utm_medium', 'utm_source', 'utm_term']

export const isFilterSet = (filterParams) => {
  if (filterParams.period !== Periods.UPCOMING) return true

  // Check if status is different from default (just 'active')
  const isDefaultStatus = filterParams.status_ids?.length === 1 && filterParams.status_ids[0] === Statuses.ACTIVE
  if (!isDefaultStatus) return true

  if (filterParams.event_type_ids?.length > 0) return true
  if (filterParams.package_uuids?.length > 0) return true
  if (filterParams.host_ids?.includes(allValue) || filterParams.host_ids?.includes(nonHostValue)) return true
  if (filterParams.invitee_emails?.length > 0) return true
  if (filterParams.team_ids?.length > 0) return true
  if (filterParams.user_ids?.length > 0) return true

  // Check UTM/Tracking ID parameters
  if (UTM_PARAMS.some((param) => filterParams[param]?.length > 0)) return true

  if (filterParams.search_keyword) return true
  if (filterParams.meeting_intelligence_status === 'COMPLETED') return true
  return false
}

export const EXTERNAL_SOURCES = {
  CALENDLY: 'CALENDLY',
  GOOGLE: 'GOOGLE',
  OUTLOOK: 'OUTLOOK'
}

/** True for connected-calendar sources (Google, Outlook); host is determined by email. */
export const isExternalCalendarSource = (source) => source === EXTERNAL_SOURCES.GOOGLE || source === EXTERNAL_SOURCES.OUTLOOK

export const getParticipantText = ({
  isHostMeeting,
  source,
  invitees,
  host,
  guestEmails,
}) => {
  const isExternalMeeting = isExternalCalendarSource(source)

  // If user is not the host, show host info
  if (!isHostMeeting) {
    // Don't show "Hosted by" for external meetings
    if (isExternalMeeting) {
      return null
    }
    // For internal meetings when host name is null, use host email
    if (!host?.name) {
      return host?.email ? I18n.t('publisher.meetings.hosted_by', { name: host.email }) : null
    }
    // For internal meetings, use host name
    return I18n.t('publisher.meetings.hosted_by', { name: host.name })
  }

  // If user is the host, show invitee info
  const firstInvitee = invitees?.[0]
  if (!firstInvitee) return null

  // For external meetings or when invitee name is null, use invitee email
  const inviteeName = isExternalMeeting || !firstInvitee.name
    ? firstInvitee.email
    : firstInvitee.name

  if (!inviteeName) return null

  // For multiple invitees, show first invitee and count
  if (invitees?.length > 1) {
    const additionalCount = invitees.length - 1
    return I18n.t('publisher.meetings.link.with_and_guests', {
      name: inviteeName,
      count: additionalCount,
      guest: additionalCount === 1 ? 'guest' : 'guests'
    })
  }

  // For single invitee with guest emails, show invitee and guest count
  if (guestEmails?.length > 0) {
    return I18n.t('publisher.meetings.link.with_and_guests', {
      name: inviteeName,
      count: guestEmails.length,
      guest: guestEmails.length === 1 ? 'guest' : 'guests'
    })
  }

  // For single invitee, just show their name/email
  return I18n.t('publisher.meetings.link.with', { name: inviteeName })
}

export const getEventLocationIfURL = (location) => {
  if (!isString(location)) return null

  if (location.match(/^https?:\/\//)) {
    return location
  }

  return null
}
